import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import LoadingScreen from '../../components/common/LoadingScreen';

const ProvinceView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: province, isLoading } = useFetchObject('provinces', 'provinces', id);
  const { handleDelete } = useDelete('provinces');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      await handleDelete(id);
      toast.success(t('provinces.delete_success'));
      navigate('/provinces');
    } catch (error) {
      console.error('Error deleting province:', error);
      toast.error(t('common.error_deleting'));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!province) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('common.not_found')}
          </Typography>
          <Button
            component={RouterLink}
            to="/provinces"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            {t('common.back_to_list')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/provinces"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {province.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('provinces.code')}: {province.code}
            </Typography>
          </Box>
          <Box>
            <Button
              component={RouterLink}
              to={`/provinces/${id}/edit`}
              variant="contained"
              startIcon={<EditIcon />}
            >
              {t('common.edit')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('provinces.basic_info')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('provinces.name')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">{province.name}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('provinces.code')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">{province.code}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Chip
                    size="small"
                    label={province.is_active ? t('common.active') : t('common.inactive')}
                    color={province.is_active ? 'success' : 'default'}
                  />
                </Grid>
                
                {province.description && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('provinces.description')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Typography variant="body1">{province.description}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.multilingual_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {province.name_dari && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('provinces.name_dari')}
                    </Typography>
                    <Typography variant="body1" dir="rtl" lang="fa">
                      {province.name_dari}
                    </Typography>
                  </Box>
                )}
                
                {province.name_pashto && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('provinces.name_pashto')}
                    </Typography>
                    <Typography variant="body1" dir="rtl" lang="ps">
                      {province.name_pashto}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
          
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.metadata')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {province.created_at && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('common.created_at')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {new Date(province.created_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {province.updated_at && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('common.updated_at')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {new Date(province.updated_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProvinceView; 